/**
 * TWS Currency Converter — v2
 * Converts .ht-money price elements between AED / USD / EUR.
 * Live rates via open.er-api.com, cached 1h in localStorage.
 */
(function () {
  'use strict';

  var CURRENCIES = ['AED', 'USD', 'EUR'];
  var SYMBOLS    = { AED: 'Dhs.', USD: 'US$', EUR: '€' };
  var RATES_KEY  = 'twc_fx_v2';
  var PREF_KEY   = 'twc_cur_v2';

  // Fallback rates when API unavailable (AED base)
  var rates = { AED: 1, USD: 0.2723, EUR: 0.2498 };
  var active = 'AED';

  try { active = localStorage.getItem(PREF_KEY) || 'AED'; } catch (e) {}

  // ── Rate management ───────────────────────────────────────
  function getCachedRates() {
    try {
      var c = JSON.parse(localStorage.getItem(RATES_KEY) || 'null');
      if (c && c.ts && Date.now() - c.ts < 3600000 && c.data) { return c.data; }
    } catch (e) {}
    return null;
  }

  function setCachedRates(data) {
    try { localStorage.setItem(RATES_KEY, JSON.stringify({ ts: Date.now(), data: data })); } catch (e) {}
  }

  function fetchRates(cb) {
    fetch('https://open.er-api.com/v6/latest/AED')
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (j && j.rates && j.rates.USD) {
          rates = { AED: 1, USD: +j.rates.USD.toFixed(6), EUR: +j.rates.EUR.toFixed(6) };
          setCachedRates(rates);
        }
        cb && cb();
      })
      .catch(function () { cb && cb(); });
  }

  // ── Price parsing ─────────────────────────────────────────
  function parseAmount(text) {
    // Strip all known currency markers, keep digits/period/comma
    var s = (text || '')
      .replace(/Dhs\.?/gi, '')
      .replace(/\bAED\b|\bUSD\b|\bEUR\b/gi, '')
      .replace(/US\$|[$€£]/g, '')
      .replace(/\s/g, '');
    // Remove thousand-separator commas (e.g. 24,500 → 24500)
    s = s.replace(/,(?=\d{3}(\D|$))/g, '');
    return parseFloat(s) || 0;
  }

  function formatNumber(aed, currency) {
    var val = aed * rates[currency];
    // Watches are high-value; no decimals for foreign currencies, 2dp for AED
    var opts = currency === 'AED'
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return val.toLocaleString('en-US', opts);
  }

  // ── DOM conversion ────────────────────────────────────────
  var converting = false;

  function convertAll(currency) {
    converting = true;

    document.querySelectorAll('.ht-money').forEach(function (el) {
      // Snapshot original AED amount on first run (before any conversion)
      if (!el.hasAttribute('data-twc-aed')) {
        // Accept any numeric price, including 0.00 (empty-cart subtotal)
        if (!/\d/.test(el.textContent || '')) return;
        var amount = parseAmount(el.textContent || '');
        el.setAttribute('data-twc-aed', String(amount));
      }

      var base = parseFloat(el.getAttribute('data-twc-aed') || '0');
      if (isNaN(base) || base < 0) return;

      var num = formatNumber(base, currency);
      // Only write if actually different — prevents MutationObserver loop
      var renderKey = currency + '|' + num;
      if (el.getAttribute('data-twc-render') === renderKey) return;
      el.setAttribute('data-twc-render', renderKey);

      if (currency === 'AED') {
        // Dirham symbol vector replaces the "Dhs." text prefix
        el.innerHTML = '<span class="visually-hidden">AED </span>' + DIRHAM_SVG + '\u00a0' + num;
      } else {
        el.textContent = SYMBOLS[currency] + '\u00a0' + num;
      }
    });

    syncUI(currency);
    converting = false;
  }

  // AED uses the official Dirham symbol (kept in sync with snippets/twc-dirham-symbol.liquid)
  var DIRHAM_SVG = '<svg class="twc-dirham" width="0.82em" height="0.71em" viewBox="0 0 344.84 299.91" fill="currentColor" aria-hidden="true" focusable="false"><path d="M342.14,140.96l2.7,2.54v-7.72c0-17-11.92-30.84-26.56-30.84h-23.41C278.49,36.7,222.69,0,139.68,0c-52.86,0-59.65,0-109.71,0,0,0,15.03,12.63,15.03,52.4v52.58h-27.68c-5.38,0-10.43-2.08-14.61-6.01l-2.7-2.54v7.72c0,17.01,11.92,30.84,26.56,30.84h18.44s0,29.99,0,29.99h-27.68c-5.38,0-10.43-2.07-14.61-6.01l-2.7-2.54v7.71c0,17,11.92,30.82,26.56,30.82h18.44s0,54.89,0,54.89c0,38.65-15.03,50.06-15.03,50.06h109.71c85.62,0,139.64-36.96,155.38-104.98h32.46c5.38,0,10.43,2.07,14.61,6l2.7,2.54v-7.71c0-17-11.92-30.83-26.56-30.83h-18.9c.32-4.88.49-9.87.49-15s-.18-10.11-.51-14.99h28.17c5.37,0,10.43,2.07,14.61,6.01ZM89.96,15.01h45.86c61.7,0,97.44,27.33,108.1,89.94l-153.96.02V15.01ZM136.21,284.93h-46.26v-89.98l153.87-.02c-9.97,56.66-42.07,88.38-107.61,90ZM247.34,149.96c0,5.13-.11,10.13-.34,14.99l-157.04.02v-29.99l157.05-.02c.22,4.84.33,9.83.33,15Z"/></svg>';
  // The picker shows flags (AED/USD/EUR alike); the Dirham mark above is for prices only
  var FLAGS = { AED: '🇦🇪', USD: '🇺🇸', EUR: '🇪🇺' };

  function syncUI(currency) {
    // Update trigger label
    var flagEl = document.getElementById('twc-cur-flag');
    var codeEl = document.getElementById('twc-cur-code');
    if (flagEl) flagEl.innerHTML = FLAGS[currency] || '';
    if (codeEl) codeEl.textContent = currency;

    // Update active state on all option items
    document.querySelectorAll('[data-twc-currency]').forEach(function (el) {
      var isActive = el.getAttribute('data-twc-currency') === currency;
      el.classList.toggle('twc-cur--active', isActive);
      if (el.hasAttribute('aria-selected')) el.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (el.hasAttribute('aria-pressed')) el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // ── Dropdown open/close ───────────────────────────────────
  function initDropdown() {
    var wrapper = document.getElementById('twc-cur-dropdown');
    var trigger = document.getElementById('twc-cur-trigger');
    var menu    = document.getElementById('twc-cur-menu');
    if (!wrapper || !trigger || !menu) return;

    function openMenu() {
      wrapper.classList.add('twc-cur--open');
      trigger.setAttribute('aria-expanded', 'true');
    }
    function closeMenu() {
      wrapper.classList.remove('twc-cur--open');
      trigger.setAttribute('aria-expanded', 'false');
    }
    function toggleMenu() {
      wrapper.classList.contains('twc-cur--open') ? closeMenu() : openMenu();
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleMenu();
    });

    // Option click
    menu.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-twc-currency]');
      if (!opt) return;
      window.twcCurrency.set(opt.getAttribute('data-twc-currency'));
      closeMenu();
    });

    // Keyboard: Enter/Space on options
    menu.addEventListener('keydown', function (e) {
      var opt = e.target.closest('[data-twc-currency]');
      if (!opt) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        window.twcCurrency.set(opt.getAttribute('data-twc-currency'));
        closeMenu();
        trigger.focus();
      }
    });

    // Mobile flat options
    document.querySelectorAll('.twc-cur--mobile [data-twc-currency]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        window.twcCurrency.set(opt.getAttribute('data-twc-currency'));
      });
    });

    // Close on outside click
    document.addEventListener('click', function () { closeMenu(); });
    wrapper.addEventListener('click', function (e) { e.stopPropagation(); });
  }

  // ── Public API ────────────────────────────────────────────
  window.twcCurrency = {
    set: function (currency) {
      if (CURRENCIES.indexOf(currency) === -1) return;
      active = currency;
      try { localStorage.setItem(PREF_KEY, currency); } catch (e) {}
      convertAll(currency);
    },
    current: function () { return active; }
  };

  // ── Init ──────────────────────────────────────────────────
  function init() {
    initDropdown();
    syncUI(active);
    convertAll(active); // always run: AED swaps "Dhs." for the Dirham symbol
  }

  var cached = getCachedRates();
  if (cached) {
    rates = cached;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } else {
    // Fetch live rates first, then init
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        fetchRates(function () { init(); });
      });
    } else {
      fetchRates(function () { init(); });
    }
  }

  // Re-apply after Shopify AJAX updates (cart, quick-buy)
  document.addEventListener('shopify:section:load', function () {
    if (!converting) convertAll(active);
  });

  // MutationObserver: re-apply when new prices are injected into the DOM
  // Guard against feedback loop with the `converting` flag
  if (window.MutationObserver) {
    var timer;
    var observer = new MutationObserver(function (mutations) {
      if (converting) return;
      // Only act if a .ht-money element was added (not just text changed by us)
      var relevant = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (
            n.classList && n.classList.contains('ht-money') ||
            n.querySelector && n.querySelector('.ht-money')
          );
        });
      });
      if (!relevant) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (!converting) convertAll(active);
      }, 200);
    });

    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
